/**
 * WidgetResultView — czysty render WYNIKU widgetu danych (Faza 23, Etap 3 T2), wydzielony
 * z `CustomWidget` (T1.8) żeby dwaj hosty (kafel pulpitu + klocek `widget_view` na formatce
 * kartoteki) dzieliły JEDEN dispatch po `result.value.kind` zamiast duplikować
 * ScalarView/TableView/CustomWidgetFailure w dwóch miejscach.
 *
 * Fetch danych, zakres firmy i obsługa stanów HTTP (loading/error) zostają U KAŻDEGO HOSTA
 * OSOBNO — różne źródła `recordId`/company scope. Ten komponent dostaje już POLICZONY wynik
 * (`WidgetComputeResult`) i tylko go renderuje — zero zapytań, zero side-effectów.
 *
 * `visualType` przychodzi z odpowiedzi endpointu danych (`WidgetDataApiResponse.visualType`) —
 * oba hosty go przekazują 1:1; pulpit dodatkowo ma zamrożony snapshot z chwili osadzenia jako
 * fallback na czas przed pierwszym fetchem (patrz `CustomWidget.tsx`). Brak/nieznany typ dla
 * serii degraduje się do słupków (bezpieczny fallback).
 */

import { useTranslation } from 'react-i18next';

import type {
  WidgetComputeFailure,
  WidgetComputeResult,
  WidgetVisualType,
} from '@/lib/widgets/types';

import { CustomWidgetChart } from './CustomWidgetChart';
import { WidgetEmpty } from './WidgetState';

function formatCell(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'number') return value.toLocaleString('pl-PL');
  if (typeof value === 'boolean') return value ? '✓' : '—';
  return String(value);
}

function ScalarView({ value }: { value: number | null }) {
  return (
    <div className="flex h-full flex-col items-center justify-center">
      <span className="text-4xl font-bold tabular-nums text-text-primary">
        {value === null ? '—' : value.toLocaleString('pl-PL')}
      </span>
    </div>
  );
}

function TableView({
  columns,
  rows,
}: {
  columns: { field: string; label: string }[];
  rows: Record<string, unknown>[];
}) {
  return (
    <div className="h-full w-full overflow-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-border-secondary text-left text-text-tertiary">
            {columns.map((c) => (
              <th key={c.field} className="px-2 py-1 font-medium">
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => (
            <tr key={idx} className="border-b border-border-secondary/50">
              {columns.map((c) => (
                <td key={c.field} className="px-2 py-1 text-text-secondary">
                  {formatCell(row[c.field])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CustomWidgetFailure({ failure }: { failure: WidgetComputeFailure }) {
  const { t } = useTranslation();
  // Brak uprawnienia do źródła → ten sam łagodny stan co reszta pulpitu. Pozostałe kody
  // (nieznane źródło / złe pole / brak kontekstu / nieobsługiwane / zła firma) = błąd konfiguracji
  // definicji — komunikat „popraw definicję w kreatorze".
  if (failure.code === 'PERMISSION_DENIED') {
    return <WidgetEmpty message={t('tenant.dashboard.permissionDenied')} />;
  }
  return <WidgetEmpty message={t('tenant.widgets.host.configError')} />;
}

export interface WidgetResultViewProps {
  result: WidgetComputeResult;
  /**
   * Typ wizualny — zwykle żywa wartość z odpowiedzi endpointu danych; pulpit może przekazać
   * zamrożony snapshot configu jako fallback przed pierwszym fetchem. Brak/nieznany typ dla
   * serii degraduje się do słupków.
   */
  visualType?: WidgetVisualType | null;
}

export function WidgetResultView({ result, visualType }: WidgetResultViewProps) {
  const { t } = useTranslation();

  if (!result.ok) return <CustomWidgetFailure failure={result.failure} />;

  const value = result.value;
  if (value.kind === 'scalar') return <ScalarView value={value.value} />;
  if (value.kind === 'series') {
    if (value.items.length === 0) return <WidgetEmpty message={t('tenant.widgets.host.noData')} />;
    return <CustomWidgetChart items={value.items} visualType={visualType ?? 'bar'} />;
  }
  // value.kind === 'rows'
  if (value.rows.length === 0) return <WidgetEmpty message={t('tenant.widgets.host.noData')} />;
  return <TableView columns={value.columns} rows={value.rows} />;
}
